/**
 * The portals the InOut calculator runs on (InOut route change, D4/D5/D7/D8):
 * Tonnie's own and its partners' (an agency, a citizenship consultancy) —
 * each with the origins allowed to frame it, its name and logo for the
 * calculator's header, and its theme. Managed by Tonnie's InOut operation;
 * read by the embed.
 *
 * A theme is TOKENS, never CSS (D4): the action colour and its text, an
 * optional page/text pair, a radius and a typeface from a closed list — each
 * pair readable (WCAG AA, 4.5:1), checked here. The colours of risk and
 * "Powered by Tonnie" are not a portal's to change.
 *
 * The handoff (D7): keeping the answer on a partner's portal sends the
 * calculation to Tonnie under a one-time token (72 h); the URL that opens
 * Tonnie carries the token, never the data.
 */
import type { InOutPortalTheme } from '@tonnie/types';
import { z } from 'zod';

import { inoutKeptCalculationSchema } from './inout';

/** `#RGB` or `#RRGGBB` — opaque, so its contrast means what it says. */
const opaqueHex = z
  .string()
  .regex(/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, 'validation.inoutPortal.colour');

/** The WCAG relative luminance of an opaque hex colour. */
function luminance(hex: string): number {
  const h = hex.slice(1);
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** The WCAG contrast ratio of two opaque hex colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG AA for text. */
export const READABLE_CONTRAST = 4.5;

/** WCAG 1.4.11 for what is not text — a focus ring, a control's edge. */
export const GRAPHIC_CONTRAST = 3;

/** The typefaces a portal may choose — each one the embed can load. */
export const INOUT_PORTAL_FONTS = [
  'system',
  'Inter',
  'DM Sans',
  'Lato',
  'Montserrat',
  'Nunito',
  'Poppins',
  'Rubik',
  'Source Sans 3',
] as const;

export const inoutPortalThemeSchema = z
  .strictObject({
    /** The calculator's action colour (buttons, progress, the chosen answer). */
    accent: opaqueHex,
    /** Text on the action colour. */
    accentForeground: opaqueHex,
    /** The card's page and its text — both or neither (Tonnie's otherwise). */
    background: opaqueHex.optional(),
    foreground: opaqueHex.optional(),
    radius: z.enum(['none', 'sm', 'md', 'lg', 'xl']),
    font: z.enum(INOUT_PORTAL_FONTS),
  })
  .superRefine((theme, ctx) => {
    if (contrastRatio(theme.accent, theme.accentForeground) < READABLE_CONTRAST) {
      ctx.addIssue({
        code: 'custom',
        path: ['accentForeground'],
        message: 'validation.inoutPortal.accentUnreadable',
      });
    }
    if ((theme.background === undefined) !== (theme.foreground === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['foreground'],
        message: 'validation.inoutPortal.pagePair',
      });
    } else if (
      theme.background &&
      theme.foreground &&
      contrastRatio(theme.background, theme.foreground) < READABLE_CONTRAST
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['foreground'],
        message: 'validation.inoutPortal.pageUnreadable',
      });
    }
  }) satisfies z.ZodType<InOutPortalTheme>;

/** A portal's code, as the loader names it (`data-portal="EI"`). */
export const inoutPortalCodeSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'validation.inoutPortal.code');

/** An origin allowed to frame the calculator — https (http only for localhost). */
export const inoutPortalOriginSchema = z
  .string()
  .regex(
    /^(?:https:\/\/[a-z0-9.-]+|http:\/\/localhost)(?::\d{1,5})?$/,
    'validation.inoutPortal.origin'
  );

/** The languages the calculator speaks — @tonnie/locale's (a spec keeps them equal). */
export const inoutPortalLocaleSchema = z.enum(['en', 'pt-PT', 'pt-BR', 'es-ES', 'it-IT']);

/** The app's colour scheme, passed to the calculator in its sheet (`?scheme=`). */
export const inoutEmbedSchemeSchema = z.enum(['light', 'dark']);

/**
 * What the app's sheet opens the calculator for (`?purpose=`): to start InOut
 * (`start`), or a new InOut in place of the one there (`new` — that one goes
 * to the person's history). Its keep step says which.
 */
export const inoutEmbedPurposeSchema = z.enum(['start', 'new']);
export type InOutEmbedPurpose = z.infer<typeof inoutEmbedPurposeSchema>;

/**
 * The app's text size passed to the calculator in its sheet (`?scale=`), as a
 * multiple of the default — the phone's Dynamic Type. Held between the
 * phone's smallest setting (iOS "xSmall", ≈ 0.82) and the 200% the
 * calculator is built to read at (WCAG 1.4.4): a larger setting gets the
 * largest the layout holds, never the default.
 */
export const INOUT_EMBED_TEXT_SCALE = { min: 0.8, max: 2 } as const;

export const inoutEmbedTextScaleSchema = z.coerce
  .number()
  .positive()
  .transform((scale) =>
    Math.min(INOUT_EMBED_TEXT_SCALE.max, Math.max(INOUT_EMBED_TEXT_SCALE.min, scale))
  );

const httpsUrl = z
  .url({ protocol: /^https$/, error: 'validation.inoutPortal.httpsUrl' })
  .max(2048, 'validation.inoutPortal.httpsUrl');

/**
 * A partner's logo from the shared static assets (`packages/assets/static/
 * partners/`, 0235), named by its path: every Tonnie web app serves it —
 * the calculator on whichever address the tier serves it from (calc.tonnie.ai,
 * its staging twin, the dev server), Citizen Web on its own.
 */
export const INOUT_PORTAL_ASSET = /^\/assets\/partners\/[a-z0-9][a-z0-9-]*\.(?:png|svg|webp)$/;

/** A portal's logo: an https address, or a partner's logo from the shared assets (`/assets/partners/…`). */
const portalLogo = z
  .string()
  .max(2048, 'validation.inoutPortal.logo')
  .refine(
    (logo) => INOUT_PORTAL_ASSET.test(logo) || httpsUrl.safeParse(logo).success,
    'validation.inoutPortal.logo'
  );

/** The grammatical gender of a portal's name — what Portuguese says before it ("na Ei!", "na Tonnie", "no portal"). */
export const inoutPortalNameGenderSchema = z.enum(['feminine', 'masculine']);

/** A portal's settings, as Tonnie's InOut operation writes them — its code is its address. */
export const inoutPortalSettingsSchema = z
  .strictObject({
    status: z.enum(['draft', 'active']),
    displayName: z
      .string()
      .trim()
      .min(1, 'validation.required')
      .max(120, 'validation.inoutPortal.nameTooLong'),
    nameGender: inoutPortalNameGenderSchema,
    logoUrl: portalLogo.nullable(),
    websiteUrl: httpsUrl,
    allowedOrigins: z
      .array(inoutPortalOriginSchema)
      .max(20, 'validation.inoutPortal.originsTooMany'),
    defaultCountry: z
      .string()
      .regex(/^[A-Z]{2}$/, 'validation.countryCode.length')
      .nullable(),
    defaultLocale: inoutPortalLocaleSchema.nullable(),
    theme: inoutPortalThemeSchema.nullable(),
    /** The agency behind the portal, when it is one (`partner_organizations.code`). */
    partnerCode: z
      .string()
      .regex(/^[A-Z][A-Z0-9_]*$/, 'validation.inoutPortal.partner')
      .nullable(),
  })
  .refine((p) => new Set(p.allowedOrigins).size === p.allowedOrigins.length, {
    path: ['allowedOrigins'],
    message: 'validation.inoutPortal.originTwice',
  })
  .refine((p) => p.status === 'draft' || p.allowedOrigins.length > 0, {
    path: ['allowedOrigins'],
    message: 'validation.inoutPortal.activeNeedsOrigin',
  });
export type InOutPortalSettingsInput = z.infer<typeof inoutPortalSettingsSchema>;

/** The operation's form for a portal: its settings and its code (set once, when it is created). */
export const inoutPortalFormSchema = inoutPortalSettingsSchema.safeExtend({
  code: inoutPortalCodeSchema,
});
export type InOutPortalFormValues = z.infer<typeof inoutPortalFormSchema>;

/**
 * What the calculator answered, said back to the person (sign-up's heading,
 * in the calculator's own words) — the answer at its top and the number of
 * trips, nothing of the record:
 *
 *   oneTrip         how long one trip from that day can last before any of
 *                   the title's limits on absence is passed (`underWay`: the
 *                   rest of a trip under way; `geography`: away from the
 *                   country, or outside the EU — where the limit counts);
 *   presenceNeeded  how many more days they need in the country;
 *   beyond          the rule that binds is already past its limit;
 *   none            no number to say (unresolved, a person checks, no rule).
 */
export const inoutCalculationAnswerSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('oneTrip'),
    days: z.number().int().min(0).max(100_000),
    underWay: z.boolean(),
    geography: z.enum(['OUTSIDE_HOST', 'OUTSIDE_EU']),
  }),
  z.strictObject({
    kind: z.literal('presenceNeeded'),
    days: z.number().int().min(0).max(100_000),
  }),
  z.strictObject({ kind: z.literal('beyond') }),
  z.strictObject({ kind: z.literal('none') }),
]);
export type InOutCalculationAnswer = z.infer<typeof inoutCalculationAnswerSchema>;

export const inoutCalculationSummarySchema = z.strictObject({
  /** The host country the answer is in (`presenceNeeded` names it). */
  countryCode: z.string().regex(/^[A-Z]{2}$/),
  trips: z.number().int().min(0).max(200),
  answer: inoutCalculationAnswerSchema,
});
export type InOutCalculationSummary = z.infer<typeof inoutCalculationSummarySchema>;

/** Keeping the answer on a portal: the calculation and what the engine answered. */
export const inoutHandoffCreateSchema = z.strictObject({
  portalCode: inoutPortalCodeSchema,
  calculation: inoutKeptCalculationSchema,
  summary: inoutCalculationSummarySchema,
});
export type InOutHandoffCreateInput = z.infer<typeof inoutHandoffCreateSchema>;

/**
 * What the calculator tells the Tonnie app that runs it in its sheet (D6,
 * `tonnie.inout.v1`): the record the person kept — with what it answered —,
 * that it wants to close, or whether there are answers closing would lose
 * (`answers`, so the sheet asks first). Only the app's WebView hears these.
 */
export const inoutAppMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('tonnie.inout.v1:result'),
    record: inoutKeptCalculationSchema.extend({ summary: inoutCalculationSummarySchema }),
  }),
  z.object({ type: z.literal('tonnie.inout.v1:close') }),
  z.object({ type: z.literal('tonnie.inout.v1:answers'), has: z.boolean() }),
]);
export type InOutAppMessage = z.infer<typeof inoutAppMessageSchema>;

/** The one-time token the URL carries — 32 random bytes, base64url. */
export const inoutHandoffTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

export const inoutHandoffAdoptSchema = z.strictObject({ token: inoutHandoffTokenSchema });
export type InOutHandoffAdoptInput = z.infer<typeof inoutHandoffAdoptSchema>;
